import React from 'react';
import { useTranslation } from 'react-i18next';
import type { CaptionMode } from '../../../types/messaging';

interface Props {
    mode: CaptionMode;
    onCycle: () => void;
}

/**
 * CaptionToggle — header CC button that single-click cycles through
 * three caption modes (always → while_playing → never). Pure SVG icon
 * (no emoji) with a subtle visual difference per state.
 */
const CaptionToggle: React.FC<Props> = ({ mode, onCycle }) => {
    const { t } = useTranslation('messaging');

    const label =
        mode === 'always'
            ? t('voice.caption.always', 'Captions: always on')
            : mode === 'while_playing'
              ? t('voice.caption.whilePlaying', 'Captions: while playing')
              : t('voice.caption.never', 'Captions: off');

    const iconColor =
        mode === 'always'
            ? 'text-white'
            : mode === 'while_playing'
              ? 'text-white/70'
              : 'text-gray-500';

    const bgColor =
        mode === 'always'
            ? 'bg-primary-500'
            : mode === 'while_playing'
              ? 'bg-primary-500/50'
              : 'bg-transparent border border-gray-300';

    return (
        <button
            type="button"
            onClick={onCycle}
            className={`
                flex items-center justify-center rounded-md w-10 h-8
                transition-colors ${bgColor} ${iconColor}
                hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-300
            `}
            aria-label={label}
            title={label}
            data-caption-mode={mode}
        >
            {/* CC pictogram */}
            <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="currentColor"
                aria-hidden="true"
            >
                <rect x="3" y="6" width="18" height="12" rx="2.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
                <text x="6" y="16" fontSize="7" fontWeight="700" fontFamily="ui-sans-serif, system-ui">CC</text>
            </svg>
        </button>
    );
};

export default CaptionToggle;
